import { Component } from '@theme/component';
import { getIOSVersion, isClickedOutside, lockScroll, unlockScroll } from '@theme/utilities';
import { StandardEvents } from '@shopify/events';

/**
 * Below this the collection grid is 2-up (~187px per card) -- too narrow for a
 * four-size row plus two buttons -- so the panel falls back to a viewport
 * sheet. Kept in sync with the same breakpoint in
 * snippets/quick-add-panel-styles.liquid.
 */
const inCardQuery = window.matchMedia('(min-width: 750px)');

/**
 * A product card's own quick-add panel.
 *
 * There is no fetch, no cache, no morph and -- crucially -- no clone. The
 * panel's markup lives inline next to the trigger (snippets/quick-add.liquid),
 * so opening it is a `show()` on an element that has been sitting in the card
 * since page load. That is what makes Shopify's payment button work: Shopify
 * upgrades each <shopify-accelerated-checkout> in place, exactly once, and a
 * copy taken out of a <template> afterwards lays out 0px wide, which silently
 * made "Buy it now" unclickable on every open after the first.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} panel - The panel itself.
 * @property {HTMLButtonElement} trigger - The circular "+" that opens it.
 *
 * @extends {Component<Refs>}
 */
export class QuickAddComponent extends Component {
  requiredRefs = ['panel', 'trigger'];

  /** Listeners that only exist while the panel is open. @type {AbortController | undefined} */
  #openController;

  disconnectedCallback() {
    super.disconnectedCallback();
    this.close();
  }

  /**
   * Whether this panel can dock inside its host card, or has to fall back to
   * the viewport sheet -- on narrow viewports, and in hosts that aren't cards
   * at all (the hotspot dialog renders a quick add too).
   */
  get #docksInCard() {
    return inCardQuery.matches && this.closest('.card-gallery') != null;
  }

  /** @param {Event} event */
  toggle = (event) => {
    event.preventDefault();

    if (this.refs.panel.open) this.close();
    else this.open();
  };

  open = () => {
    const { panel, trigger } = this.refs;

    if (panel.open) return;

    // One open panel per page: two cards showing size rows at once reads as a
    // stuck UI rather than a comparison.
    for (const other of document.querySelectorAll('quick-add-component')) {
      if (other !== this && other instanceof QuickAddComponent) other.close();
    }

    if (this.#docksInCard) {
      // Non-modal, so the panel stays in normal flow and can be positioned
      // against the card it belongs to.
      //
      // Deliberately no scroll-into-view here. The panel docks to the bottom
      // edge of a card the shopper just hovered and clicked, so the card is
      // already on screen, and the panel is capped at `max-height: 100%` of
      // that card (quick-add-panel-styles.liquid) -- it cannot render below a
      // card that is itself visible. An earlier scrollIntoView() guarded a
      // case that can't happen (you can't click a trigger that's off-screen),
      // and its real effect was to scroll the store's inner `.page-wrapper`
      // scroll container on nearly every open -- read as the product image
      // "jumping". `html`/`body` are `overflow: hidden`, so that scroll never
      // touched window.scrollY, which is why it was so hard to see.
      panel.show();
    } else {
      // Modal, so the panel enters the top layer -- the only way out of
      // .card-gallery's `container-type: inline-size`, whose layout
      // containment would otherwise make the card, not the viewport, the
      // containing block for the sheet's `position: fixed`.
      panel.showModal();
      lockScroll(panel);
      // No scroll-into-view here: inside the top layer, `position: fixed`
      // (quick-add-panel-styles.liquid) resolves against the viewport, not
      // the card, so this sheet is always pinned to the viewport's bottom
      // edge already, wherever the card sits. lockScroll has also just made
      // the scroll container non-scrollable, so a scroll attempt would
      // silently no-op anyway -- confirmed empirically, see verification.
    }

    this.toggleAttribute('open', true);
    trigger.setAttribute('aria-expanded', 'true');

    const { signal } = (this.#openController = new AbortController());

    document.addEventListener('pointerdown', this.#handleOutsidePointerDown, { signal });
    document.addEventListener('keydown', this.#handleKeyDown, { signal });
    // A resize across the breakpoint would leave a non-modal panel wearing the
    // sheet's styles (or vice versa); reopening is cheap, reconciling is not.
    inCardQuery.addEventListener('change', this.close, { signal });
    this.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartUpdate, { signal });
    // Escape closes a modal dialog natively, without going through close().
    panel.addEventListener('close', this.close, { signal });
  };

  close = () => {
    const { panel, trigger } = this.refs;

    this.#openController?.abort();
    this.#openController = undefined;

    if (panel.open) panel.close();

    unlockScroll(panel);
    this.removeAttribute('open');
    trigger.setAttribute('aria-expanded', 'false');
    this.#patchIOS16GridFreeze();
  };

  /** @param {PointerEvent} event */
  #handleOutsidePointerDown = (event) => {
    const { panel, trigger } = this.refs;

    // The trigger sits outside the panel but toggles it, so leave it to its
    // own click handler -- otherwise the press closes and the click reopens.
    if (event.target instanceof Node && trigger.contains(event.target)) return;

    if (isClickedOutside(event, panel)) this.close();
  };

  /** @param {KeyboardEvent} event */
  #handleKeyDown = (event) => {
    if (event.key === 'Escape') this.close();
  };

  /**
   * Closes the panel on a successful cart update -- the cart drawer opens over
   * it anyway, and leaving it behind hides the card the shopper just bought.
   *
   * @param {import('@shopify/events').CartLinesUpdateEvent} event
   */
  #handleCartUpdate = (event) => {
    event.promise
      ?.then(({ detail }) => {
        if (detail?.didError) return;
        this.close();
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[quick-add] Event promise rejected:', error);
      });
  };

  /**
   * Patch for the UI freezing after the panel closes. To reproduce it, use
   * iOS 16.0.
   */
  #patchIOS16GridFreeze() {
    const iosVersion = getIOSVersion();

    if (!iosVersion || iosVersion.major >= 17 || (iosVersion.major === 16 && iosVersion.minor >= 4)) return;

    requestAnimationFrame(() => {
      /** @type {HTMLElement | null} */
      const grid = document.querySelector('#ResultsList [product-grid-view]');
      if (!grid) return;

      const currentWidth = grid.getBoundingClientRect().width;
      grid.style.width = `${currentWidth - 1}px`;
      requestAnimationFrame(() => {
        grid.style.width = '';
      });
    });
  }
}

if (!customElements.get('quick-add-component')) {
  customElements.define('quick-add-component', QuickAddComponent);
}
